import dayjs from "dayjs";
import { canRescheduleAppointment } from "@/pages/adminInsights/utils/matchConsultationToPayment";

export interface RescheduleAppointmentEntry {
  appointment: fhir5.Appointment;
  patientId: string;
  patientName: string;
  dateLabel: string;
  timeLabel: string;
}

export interface ReschedulePatientGroup {
  patientId: string;
  patientName: string;
  appointments: RescheduleAppointmentEntry[];
}

function pickPatientName(patient: fhir5.Patient): string {
  const nameObj =
    patient.name?.find((n) => n.use === "usual" || n.use === "official") ??
    patient.name?.[0];
  const fromText = String(nameObj?.text ?? "").trim();
  if (fromText) return fromText;
  const fromGiven = nameObj?.given?.join(" ")?.trim();
  return fromGiven || "Paciente";
}

export function getAppointmentPatientId(
  appointment: fhir5.Appointment
): string | null {
  const reference = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith("Patient/")
  )?.actor?.reference;
  return reference?.split("/")[1] ?? null;
}

export function getPatientNameForAppointment(
  appointment: fhir5.Appointment,
  patients: fhir5.Patient[]
): string {
  const patientId = getAppointmentPatientId(appointment);
  if (!patientId) return "Paciente";
  const patient = patients.find((entry) => entry.id === patientId);
  return patient ? pickPatientName(patient) : "Paciente";
}

export function buildRescheduleEntries(
  appointments: fhir5.Appointment[],
  patients: fhir5.Patient[]
): RescheduleAppointmentEntry[] {
  return appointments
    .filter((appointment) => canRescheduleAppointment(appointment))
    .map((appointment) => ({
      appointment,
      patientId: getAppointmentPatientId(appointment) ?? "sem-paciente",
      patientName: getPatientNameForAppointment(appointment, patients),
      dateLabel: appointment.start
        ? dayjs(appointment.start).format("DD/MM/YYYY")
        : "—",
      timeLabel: appointment.start
        ? dayjs(appointment.start).format("HH:mm")
        : "—",
    }))
    .sort((left, right) => {
      const byPatient = left.patientName.localeCompare(right.patientName, "pt-BR");
      if (byPatient !== 0) return byPatient;
      return dayjs(left.appointment.start).valueOf() -
        dayjs(right.appointment.start).valueOf();
    });
}

export function groupRescheduleEntriesByPatient(
  entries: RescheduleAppointmentEntry[]
): ReschedulePatientGroup[] {
  const groups = new Map<string, ReschedulePatientGroup>();

  entries.forEach((entry) => {
    const existing = groups.get(entry.patientId);
    if (existing) {
      existing.appointments.push(entry);
      return;
    }

    groups.set(entry.patientId, {
      patientId: entry.patientId,
      patientName: entry.patientName,
      appointments: [entry],
    });
  });

  return Array.from(groups.values()).sort((left, right) =>
    left.patientName.localeCompare(right.patientName, "pt-BR")
  );
}
